import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, Badge, Spinner, ProgressBar, Table, Alert } from 'react-bootstrap'
import { Document, Page, pdfjs } from 'react-pdf'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'
import { baseUrl } from 'api/helpers'
import { formatDateLocal } from 'helpers/datetime'
import { useUploadReceipt, useUnmatchReceiptScan } from 'api/hooks/use-receipt-scans'
import ReceiptDropzone from 'receipt-scans/ReceiptDropzone'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const ReceiptTab = ({ receiptScan, transactionDetailId }) => {
  const isInvoice = receiptScan?.scan_type === 'invoice'
  const [showModal, setShowModal] = useState(false)
  const [showMatchDetails, setShowMatchDetails] = useState(false)
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [modalPageNumber, setModalPageNumber] = useState(1)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState(null)
  const queryClient = useQueryClient()

  const uploadMutation = useUploadReceipt({
    scanType: isInvoice ? 'invoice' : 'receipt',
    options: {
      onSuccess: () => {
        toast.success('Receipt uploaded successfully. Processing will begin shortly.')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['transactions'])
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to upload receipt'
        toast.error(errorMessage)
      }
    }
  })

  const unmatchMutation = useUnmatchReceiptScan({
    options: {
      onSuccess: () => {
        toast.success('Receipt detached successfully')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['transactions'])
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to detach receipt'
        toast.error(errorMessage)
      }
    }
  })

  const handleDetach = () => {
    if (window.confirm('Are you sure you want to detach this receipt from the transaction?')) {
      unmatchMutation.mutate(receiptScan.id)
    }
  }

  if (!receiptScan || !receiptScan.receipt_image_url) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-4">No {isInvoice ? 'invoice' : 'receipt'} attached to this transaction.</p>
        <Alert variant="info" className="mb-4">
          {isInvoice
            ? 'Upload an invoice to attach it to this transaction. The system will automatically match it based on OCR data.'
            : 'Upload a receipt to attach it to this transaction. The system will automatically match it based on OCR data.'
          }
        </Alert>
        <ReceiptDropzone onUploadSuccess={() => {}} scanType={isInvoice ? 'invoice' : 'receipt'} />
      </div>
    )
  }

  // Construct full image URL by prepending API base URL
  const imageUrl = receiptScan.receipt_image_url
    ? `${baseUrl}${receiptScan.receipt_image_url}`
    : null

  // Check if the file is a PDF
  const isPdf = receiptScan.file_name?.toLowerCase().endsWith('.pdf')

  // Get match details from match_candidates
  const matchDetails = receiptScan.match_candidates?.[0] || null

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A'
    return `${Math.round(value * 100)}%`
  }

  const getMatchTypeBadge = (matchType) => {
    if (!matchType) return null
    const typeMap = {
      auto: { className: 'badge-success', text: 'Auto Matched' },
      manual: { className: 'badge-primary', text: 'Manually Matched' }
    }
    const config = typeMap[matchType] || { className: 'badge-secondary', text: matchType }
    return <Badge bg="" className={`${config.className} small`}>{config.text}</Badge>
  }

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'secondary'
    if (score >= 0.8) return 'success'
    if (score >= 0.5) return 'warning'
    return 'danger'
  }

  const getScoreLabel = (key) => {
    const labels = {
      amount: 'Amount',
      vendor_name: 'Vendor Name',
      date: 'Date',
      card_number: 'Card Number',
      invoice_number: 'Invoice Number',
      po_number: 'PO Number'
    }
    return labels[key] || key
  }

  const handleDownload = () => {
    window.open(imageUrl, '_blank')
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setPdfLoading(false)
    setPdfError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error)
    setPdfLoading(false)
    setPdfError('Failed to load PDF')
  }

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages))
  }

  const goToModalPrevPage = () => {
    setModalPageNumber((prev) => Math.max(prev - 1, 1))
  }

  const goToModalNextPage = () => {
    setModalPageNumber((prev) => Math.min(prev + 1, numPages))
  }

  const openPdfModal = () => {
    setModalPageNumber(pageNumber) // Start at current page
    setShowModal(true)
  }

  return (
    <>
      <div className="receipt-tab">
        {/* Match Info - Clickable */}
        <div
          className="mb-3 p-3 bg-light rounded d-flex justify-content-between align-items-center"
          style={{ cursor: matchDetails ? 'pointer' : 'default' }}
          onClick={() => matchDetails && setShowMatchDetails(true)}
        >
          <div className="d-flex align-items-center gap-2">
            {getMatchTypeBadge(receiptScan.match_type)}
            {receiptScan.match_confidence !== null && receiptScan.match_confidence !== undefined && (
              <Badge bg="" className="badge-info small">
                {Math.round(receiptScan.match_confidence * 100)}% confidence
              </Badge>
            )}
          </div>
          {matchDetails && (
            <small className="text-muted">
              Click to view match details →
            </small>
          )}
        </div>

        {/* Extracted Data (effective = user override or OCR) */}
        {(receiptScan.effective_vendor_name || receiptScan.effective_total_amount) && (
          <div className="bg-light p-3 rounded mb-4">
            <h6 className="mb-3">Extracted Information</h6>
            <div className="row">
              {receiptScan.effective_vendor_name && (
                <div className="col-md-6 mb-2">
                  <small className="text-muted d-block">Vendor</small>
                  <span>{receiptScan.effective_vendor_name}</span>
                </div>
              )}
              {receiptScan.effective_total_amount && (
                <div className="col-md-6 mb-2">
                  <small className="text-muted d-block">Amount</small>
                  <span>{formatCurrency(receiptScan.effective_total_amount)}</span>
                </div>
              )}
              {receiptScan.effective_transaction_date && (
                <div className="col-md-6 mb-2">
                  <small className="text-muted d-block">Date</small>
                  <span>{formatDateLocal(receiptScan.effective_transaction_date)}</span>
                </div>
              )}
              {isInvoice ? (
                <>
                  {receiptScan.effective_invoice_number && (
                    <div className="col-md-6 mb-2">
                      <small className="text-muted d-block">Invoice #</small>
                      <span>{receiptScan.effective_invoice_number}</span>
                    </div>
                  )}
                  {receiptScan.effective_po_number && (
                    <div className="col-md-6 mb-2">
                      <small className="text-muted d-block">PO #</small>
                      <span>{receiptScan.effective_po_number}</span>
                    </div>
                  )}
                  {receiptScan.effective_due_date && (
                    <div className="col-md-6 mb-2">
                      <small className="text-muted d-block">Due Date</small>
                      <span>{formatDateLocal(receiptScan.effective_due_date)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {receiptScan.effective_card_last_four && (
                    <div className="col-md-6 mb-2">
                      <small className="text-muted d-block">Card Last 4</small>
                      <span>****{receiptScan.effective_card_last_four}</span>
                    </div>
                  )}
                  {receiptScan.effective_invoice_number && (
                    <div className="col-md-6 mb-2">
                      <small className="text-muted d-block">Invoice #</small>
                      <span>{receiptScan.effective_invoice_number}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Receipt - Click to enlarge */}
        <div className="text-center mb-4">
          {isPdf ? (
            <div
              className="pdf-viewer-container rounded"
              style={{ border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}
            >
              {pdfLoading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <span className="ms-2">Loading PDF...</span>
                </div>
              )}

              {pdfError && (
                <div className="text-center py-5">
                  <p className="text-danger mb-3">{pdfError}</p>
                  <Button variant="primary" onClick={handleDownload}>
                    Open PDF in New Tab
                  </Button>
                </div>
              )}

              <div
                style={{ cursor: 'pointer' }}
                onClick={openPdfModal}
                title="Click to expand"
              >
                <Document
                  file={imageUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading=""
                >
                  <Page
                    pageNumber={pageNumber}
                    width={400}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              </div>

              {!pdfLoading && !pdfError && (
                <div className="text-muted small mt-2 mb-2">
                  Click to expand
                </div>
              )}

              {!pdfLoading && !pdfError && numPages && (
                <div className="d-flex justify-content-center align-items-center gap-3 py-2 border-top">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-muted small">
                    Page {pageNumber} of {numPages}
                  </span>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={openPdfModal}
                  >
                    Expand
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => setShowModal(true)}
            >
              <img
                src={imageUrl}
                alt="Receipt"
                className="img-fluid rounded shadow-sm"
                style={{
                  maxHeight: '400px',
                  border: '1px solid #dee2e6'
                }}
              />
              <div className="text-muted small mt-2">
                Click image to view full size
              </div>
            </div>
          )}
        </div>

        {/* Upload Info and Actions */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Uploaded {formatDateLocal(receiptScan.created_at)}
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={handleDetach}
            disabled={unmatchMutation.isLoading}
          >
            {unmatchMutation.isLoading ? 'Detaching...' : (isInvoice ? 'Detach Invoice' : 'Detach Receipt')}
          </Button>
        </div>
      </div>

      {/* Match Details Modal */}
      <Modal
        show={showMatchDetails}
        onHide={() => setShowMatchDetails(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Match Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchDetails && (
            <>
              {/* Overall Score */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Overall Match Score</h6>
                  <Badge bg="" className={`badge-${getScoreColor(matchDetails.total_score)} fs-6`}>
                    {formatPercent(matchDetails.total_score)}
                  </Badge>
                </div>
                <ProgressBar
                  now={(matchDetails.total_score || 0) * 100}
                  variant={getScoreColor(matchDetails.total_score)}
                />
              </div>

              {/* Individual Scores */}
              <h6 className="mb-3">Score Breakdown</h6>
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Field</th>
                    <th>Score</th>
                    <th>Weight</th>
                    <th>Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {matchDetails.scores && Object.entries(matchDetails.scores).map(([key, score]) => {
                    const weight = matchDetails.weights?.[key]
                    const contribution = score !== null && weight ? score * weight : null

                    return (
                      <tr key={key}>
                        <td>{getScoreLabel(key)}</td>
                        <td>
                          {score !== null ? (
                            <Badge bg="" className={`badge-${getScoreColor(score)}`}>{formatPercent(score)}</Badge>
                          ) : (
                            <span className="text-muted">No data</span>
                          )}
                        </td>
                        <td>
                          {weight ? formatPercent(weight) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {contribution !== null ? (
                            formatPercent(contribution)
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>

              {/* Matched Transaction Info */}
              {matchDetails.matched_transaction && (
                <>
                  <h6 className="mb-3 mt-4">Matched Transaction</h6>
                  <div className="bg-light p-3 rounded">
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Amount</small>
                        <span>{formatCurrency(matchDetails.matched_transaction.amount)}</span>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Date</small>
                        <span>{formatDateLocal(matchDetails.matched_transaction.date)}</span>
                      </div>
                      {matchDetails.matched_transaction.vendor_name && (
                        <div className="col-md-6 mb-2">
                          <small className="text-muted d-block">Vendor</small>
                          <span>{matchDetails.matched_transaction.vendor_name}</span>
                        </div>
                      )}
                      {matchDetails.matched_transaction.employee_name && (
                        <div className="col-md-6 mb-2">
                          <small className="text-muted d-block">Employee</small>
                          <span>{matchDetails.matched_transaction.employee_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Comparison */}
              <h6 className="mb-3 mt-4">Comparison</h6>
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Field</th>
                    <th>{isInvoice ? 'Invoice' : 'Receipt'}</th>
                    <th>Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Amount</td>
                    <td>{formatCurrency(receiptScan.effective_total_amount)}</td>
                    <td>{formatCurrency(matchDetails.matched_transaction?.amount)}</td>
                  </tr>
                  <tr>
                    <td>Date</td>
                    <td>{formatDateLocal(receiptScan.effective_transaction_date)}</td>
                    <td>{formatDateLocal(matchDetails.matched_transaction?.date)}</td>
                  </tr>
                  <tr>
                    <td>Vendor</td>
                    <td>{receiptScan.effective_vendor_name || 'N/A'}</td>
                    <td>{matchDetails.matched_transaction?.vendor_name || 'N/A'}</td>
                  </tr>
                  {isInvoice ? (
                    <>
                      <tr>
                        <td>Invoice #</td>
                        <td>{receiptScan.effective_invoice_number || 'N/A'}</td>
                        <td>{matchDetails.matched_transaction?.invoice_number || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>PO #</td>
                        <td>{receiptScan.effective_po_number || 'N/A'}</td>
                        <td>{matchDetails.matched_transaction?.po_number || 'N/A'}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td>Card Last 4</td>
                      <td>{receiptScan.effective_card_last_four ? `****${receiptScan.effective_card_last_four}` : 'N/A'}</td>
                      <td>-</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShowMatchDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Full Size Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isPdf ? (isInvoice ? 'Invoice PDF' : 'Receipt PDF') : (isInvoice ? 'Invoice Image' : 'Receipt Image')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
          {isPdf ? (
            <div className="d-flex flex-column align-items-center">
              <Document
                file={imageUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading PDF...</span>
                  </div>
                }
              >
                <Page
                  pageNumber={modalPageNumber}
                  width={700}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
              {numPages && numPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={goToModalPrevPage}
                    disabled={modalPageNumber <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-muted">
                    Page {modalPageNumber} of {numPages}
                  </span>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={goToModalNextPage}
                    disabled={modalPageNumber >= numPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <img
              src={imageUrl}
              alt="Receipt"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

ReceiptTab.propTypes = {
  receiptScan: PropTypes.object,
  transactionDetailId: PropTypes.string
}

export default ReceiptTab
